import { create } from 'zustand';
import { coreApi } from '@/integrations/api/active';
import { supabase } from '@/integrations/supabase/client';

export const NUDGE_WINDOW_MS = 24 * 60 * 60 * 1000;

let _nudgesRequestGeneration = 0;
export const NUDGE_MESSAGE_MAX = 280;

export type NudgeEntityType = 'rock' | 'metric' | 'issue' | 'task' | 'idea' | 'process';

export type NudgeRefusal =
  | 'rate-limited'
  | 'not-permitted'
  | 'closed'
  | 'not-visible'
  | 'invalid'
  | 'failed';

const REFUSAL_BY_SQLSTATE: Record<string, NudgeRefusal> = {
  NG001: 'rate-limited',
  NG002: 'not-permitted',
  NG003: 'closed',
  NG004: 'not-visible',
  NG005: 'invalid',
};

export interface SentNudge {
  id: string;
  recipientUserId: string;
  entityId: string | null;
  createdAt: string;
}

export interface SendNudgeArgs {
  workspaceId: string;
  recipientUserId: string;
  message: string;
  entityType?: NudgeEntityType;
  entityId?: string;
}

export type SendNudgeResult =
  | { ok: true }
  | { ok: false; reason: NudgeRefusal; message: string };

interface NudgesState {
  sent: SentNudge[];
  loadedWorkspaceId: string | null;

  fetchMySent: (workspaceId: string) => Promise<void>;
  sendNudge: (args: SendNudgeArgs) => Promise<SendNudgeResult>;
  cooldownUntilMs: (recipientUserId: string, entityId?: string | null) => number | null;
  reset: () => void;
}

/** Nudges-owned server cache, keyed by `loadedWorkspaceId`. */
export const useNudgesStore = create<NudgesState>((set, get) => ({
  sent: [],
  loadedWorkspaceId: null,

  reset: () => {
    _nudgesRequestGeneration += 1;
    set({ sent: [], loadedWorkspaceId: null });
  },

  fetchMySent: async (workspaceId) => {
    const requestGeneration = ++_nudgesRequestGeneration;
    if (get().loadedWorkspaceId !== workspaceId) set({ sent: [] });
    const { data: authData } = await supabase.auth.getUser();
    if (!authData.user) return;
    const { data, error } = await supabase
      .from('nudges')
      .select('id, recipient_user_id, entity_id, created_at')
      .eq('workspace_id', workspaceId)
      .eq('sender_user_id', authData.user.id)
      .gte('created_at', new Date(Date.now() - NUDGE_WINDOW_MS).toISOString());
    if (requestGeneration !== _nudgesRequestGeneration) return;
    if (error) { console.error('[nudges] fetch failed', error); return; }
    set({
      loadedWorkspaceId: workspaceId,
      sent: (data ?? []).map((r) => ({
        id: r.id,
        recipientUserId: r.recipient_user_id,
        entityId: r.entity_id ?? null,
        createdAt: r.created_at,
      })),
    });
  },

  sendNudge: async ({ workspaceId, recipientUserId, message, entityType, entityId }) => {
    const { data: sent, error } = await coreApi('/nudges', {
      workspace_id: workspaceId,
      recipient_user_id: recipientUserId,
      message,
      ...(entityType ? { entity_type: entityType } : {}),
      ...(entityId ? { entity_id: entityId } : {}),
    });
    if (error) {
      console.error('[nudges] send failed', error);
      return {
        ok: false,
        reason: REFUSAL_BY_SQLSTATE[error.code ?? ''] ?? 'failed',
        message: error.details || error.message,
      };
    }
    const data = (sent?.[0] as { id?: string | null } | undefined)?.id ?? null;
    set((s) => ({
      sent: [
        ...s.sent,
        {
          id: (data as string | null) ?? crypto.randomUUID(),
          recipientUserId,
          entityId: entityId ?? null,
          createdAt: new Date().toISOString(),
        },
      ],
    }));
    return { ok: true };
  },

  cooldownUntilMs: (recipientUserId, entityId) => {
    const key = entityId ?? null;
    const latest = get().sent
      .filter((p) => p.recipientUserId === recipientUserId && p.entityId === key)
      .map((p) => new Date(p.createdAt).getTime())
      .sort((a, b) => b - a)[0];
    if (latest === undefined) return null;
    const until = latest + NUDGE_WINDOW_MS;
    return until > Date.now() ? until : null;
  },
}));
